import { Button } from "@components/ui/Button"
import { SearchResult, SearchResults } from "@features/search/useSearchResults"
import { cn, tv } from "@utils/tw"
import { AnimatePresence, motion } from "motion/react"
import { Fragment, ReactNode, useMemo } from "react"
import { SFFRagDocumentTypeName } from "../../server/search/rag"
import { FilmSearchResult, StandardSearchResult } from "./cards/StandardSearchResult"
import { useSearch } from "./useSearchInput"
import { AnimatedSearchCards } from "./utils/AnimatedSearchResults"
import { SearchResultLink } from "./utils/SearchResultLink"
import { SearchResultWidget } from "./utils/SearchResultWidget"
import { FilmTile } from "@components/film/FilmTile"
import { BrowseFilmTileFragment } from "@generated-types"
import { Link } from "eddev/routing"
import { IconChevronRight } from "@components/ui/Icon"

type Props = {
  types: SFFRagDocumentTypeName[]
  setTypes: (types: SFFRagDocumentTypeName[]) => void
  search: SearchResults
  isSmall?: boolean
  isFull?: boolean
}

export function SearchResultsDisplay(props: Props) {
  const { search, types, setTypes, isSmall } = props

  const searchState = useSearch()

  const styles = resultStyles({
    isSmall: isSmall,
  })

  const query = useSearch().input.searchQuery

  const totalResults = search.results.length
  const hasResults = totalResults > 0
  const hasSearched = query.length > 0

  // Tabs for filtering results
  let tabs: ReactNode
  const enabledTabs = search.tabs.filter((tab) => tab.enabled)
  tabs = (
    <div className="flex flex-row gap-[8px] w-min">
      <Button
        variant={types.length == 0 ? "primary" : "default"}
        onClick={(e) => {
          setTypes([])
        }}
        disabled={enabledTabs.length === 0}
      >
        All
      </Button>
      <AnimatePresence mode="popLayout" presenceAffectsLayout>
        {(enabledTabs.length === 0 ? search.tabs : enabledTabs).map((tab) => {
          return (
            <motion.div
              key={tab.key}
              layout="position"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Button
                variant={tab === search.activeTab ? "primary" : "default"}
                onClick={(e) => {
                  setTypes(tab.types ?? [])
                }}
                disabled={!tab.enabled}
              >
                {tab.label}
              </Button>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )

  // Suggested searches + links
  const suggestionResults = search.recommendations.length > 0 && (
    <>
      {search.recommendations.map((group, i) => {
        return (
          <div key={group.heading ?? i} data-mixpanel-suggested-link-group={group.heading}>
            <div className={styles.sectionHeader()}>{group.heading}</div>
            <div className={cn("flex flex-col text-left mb-0 gap-1", !isSmall && "md:items-start mb-7")}>
              {group.items.map((item, i) => {
                return <SearchResultLink key={i} {...item} />
              })}
            </div>
          </div>
        )
      })}
    </>
  )

  const centerItems = useMemo(() => {
    return search.results.filter((item) => {
      return item.kind !== "widget" || item.widget.placement === "center"
    })
  }, [search.results])

  function renderResultNode(item: SearchResult): ReactNode {
    let node: ReactNode | null = null
    if (item.kind === "widget") {
      node = <SearchResultWidget>{item.widget.node}</SearchResultWidget>
    } else if (item.kind === "document") {
      node = item.node
    } else if (item.kind === "filmGroup") {
      node = (
        <div>
          {/* <h4 className={cn(styles.sectionHeader(), "max-md:mb-2")}>{item.heading}</h4> */}
          {item.films.map((film) => {
            return (
              <StandardSearchResult
                key={film?.uri}
                item={{
                  type: "film",
                  data: film as any,
                  id: 0,
                  score: 0,
                  uri: film?.uri!,
                  title: film?.title!,
                }}
              />
            )
          })}
        </div>
      )
    }
    if (!node) return null
    return (
      <motion.div
        layout
        // layout="position"
        // layoutScroll
        layoutId={item.key}
        key={item.key}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {node}
      </motion.div>
    )
  }

  // The center results (which is the only result set on mobile)
  const loadingMoreResults = searchState.rawResults.vectorLoading || searchState.rawResults.intentLoading
  const mainResults = useMemo(() => {
    let nodes: ReactNode[] = []

    let hasLocalResults = false
    let hasIntentResults = false
    let hasVectorResults = false

    let currentHeading = ""

    const addNode = (node: ReactNode, heading: string | null, headingWidget?: ReactNode) => {
      if (heading && heading !== currentHeading) {
        nodes.push(
          <motion.h4
            key={heading}
            className={cn("type-label-caps text-fg-secondary flex justify-between", nodes.length > 0 && "pt-[1em]")}
            layoutId={`heading-${heading}`}
            layout="position"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {heading}
            {headingWidget}
          </motion.h4>,
        )
        currentHeading = heading
      }
      if (node) nodes.push(node)
    }

    // Render local results first
    const localResults = centerItems
      .filter((item) => item.kind === "document" && item.document.isLocalResult)
      .map((item, i) => <Fragment key={i}>{renderResultNode(item)}</Fragment>)
      .filter(Boolean)
    for (const item of localResults) {
      hasLocalResults = true
      addNode(item, localResults.length > 1 ? "Closest Matches" : "Closest Match")
    }

    // Loading state...
    if (loadingMoreResults) {
      // addNode(
      //   <motion.div layoutId="loading-more" layout="position" className="bg-bg-low rounded-[12px] p-[12px] type-ui-m">
      //     Loading...
      //   </motion.div>,
      //   null,
      // )
    }

    // if (searchState.rawResults.intent) {
    //   addNode(
    //     <motion.div
    //       key="intent-debug"
    //       layoutId="intent-debug"
    //       layout="position"
    //       className="bg-bg-low rounded-[12px] p-[12px] type-ui-m"
    //       initial={{ opacity: 0 }}
    //       animate={{ opacity: 1 }}
    //     >
    //       <pre className="h-[100px] overflow-y-auto">
    //         DEBUG:{JSON.stringify({ ...searchState.rawResults.intent, topResults: undefined }, null, 2)}
    //       </pre>
    //     </motion.div>,
    //     "Debug",
    //   )
    // }

    const intentResult = searchState.results.intent.result
    if (intentResult) {
      if (
        intentResult.type === "events" &&
        intentResult.tiles.length &&
        (types.includes("film") || types.length === 0)
      ) {
        hasIntentResults = true
        addNode(
          <motion.div
            key="intent-results"
            layout="position"
            layoutId="intent-events"
            className="flex flex-col gap-[4px]"
          >
            {/* <h4 className={cn(styles.sectionHeader(), "max-md:mb-2")}>{intentResult.heading}</h4> */}
            {intentResult.tiles.slice(0, props.isSmall ? 6 : 8).map((tile, index) => (
              <FilmSearchResult key={tile.filmId} {...(tile.film as BrowseFilmTileFragment)} session={tile.session!} />
            ))}
          </motion.div>,
          intentResult.heading,
          intentResult.link && (
            <Link href={intentResult.link} className="inline-flex text-fg-secondary hover:text-fg items-center">
              More like this <IconChevronRight className="my-[-20px] size-[16px]" />
            </Link>
          ),
        )
      }
    }

    // Non-local results next
    const nonLocalResults = centerItems.filter((item) => item.kind !== "document" || !item.document.isLocalResult)
    for (const item of nonLocalResults) {
      let node = renderResultNode(item)
      if (item.kind === "widget") {
        node = (
          <div data-search-item className="py-[4px]">
            {node}
          </div>
        )
      }
      addNode(
        node,
        item.kind === "filmGroup" ? item.heading : hasLocalResults || hasIntentResults ? "More Results" : "Top Results",
      )
    }

    return <>{nodes}</>
  }, [centerItems, loadingMoreResults])

  // const mainResults = (
  //   <>
  //     {/* Render local results first */}
  //     {searchState.rawResults.vectorLoading && <div>Loading Vector</div>}
  //     {searchState.rawResults.intentLoading && <div>Loading Intent</div>}
  //     {searchState.rawResults.intent && (
  //       <pre className="h-[100px] overflow-y-auto">
  //         {JSON.stringify({ ...searchState.rawResults.intent, topResults: undefined }, null, 2)}
  //       </pre>
  //     )}
  //     {centerItems
  //       .filter((item) => item.kind !== "document" || !item.document.isLocalResult)
  //       .map(renderResultNode)
  //       .filter(Boolean)}
  //   </>
  // )

  // The right side widgets. On mobile, these are incorporated into the main results
  const widgetResults = (
    <div className="flex flex-col gap-[8px]">
      {search.results
        .filter((item) => item.kind === "widget" && item.widget.placement === "side")
        .map((item, index) => {
          if (item.kind !== "widget" || item.widget.placement !== "side") return null
          return (
            <motion.div
              layout="position"
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {item.widget.node}
            </motion.div>
          )
        })}
    </div>
  )

  if (isSmall) {
    return (
      <div data-mixpanel-area="Search Results Page">
        <div className="w-full pt-4">
          <AnimatePresence>
            {enabledTabs.length > 0 && (
              <motion.div
                className="mb-6 md:hidden"
                exit={{ opacity: 0, height: 0 }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <div className="overflow-auto py-2 -mx-grid-margin px-grid-margin scrollbars-hidden">{tabs}</div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-col gap-[14px] h-min">
            {suggestionResults}

            {centerItems.length > 0 && (
              <AnimatedSearchCards className="flex flex-col gap-[4px] h-min">{mainResults}</AnimatedSearchCards>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="full-results" data-mixpanel-area="Search Results Page">
      <div className={"grid-auto"}>
        {/* Header */}
        <div className={cn("col-span-12 md:col-span-3 py-6 hidden md:block")}>
          <h2 className="type-title-m">Search Results</h2>
        </div>

        <div className="col-span-12 md:col-span-9 mb-6 mt-4 md:mt-6">
          <div className="max-md:overflow-auto max-md:py-2 max-md:-mx-grid-margin max-md:px-grid-margin scrollbars-hidden">
            {tabs}
          </div>
        </div>

        <div className="col-span-12 md:col-span-3" data-mixpanel-section="Search Suggestions">
          {suggestionResults}
        </div>

        <div className="col-span-12 md:col-span-5 flex flex-col" data-mixpanel-section="Search Main Results">
          {/* <h4 className={cn(styles.sectionHeader(), "max-md:mb-2", !hasSearched && "hidden")}>
            {totalResults === 1
              ? "1 result found"
              : totalResults === 0
                ? "No results were found"
                : `${totalResults} results found`}
          </h4> */}
          <div className="flex flex-col gap-[4px]">{mainResults}</div>
        </div>

        <div className="col-span-12 md:col-span-4 flex flex-col" data-mixpanel-section="Search Widget results">
          <h4 className={cn(styles.sectionHeader())}></h4>
          <AnimatedSearchCards>{widgetResults}</AnimatedSearchCards>
        </div>
      </div>
      <div className="col-span-12 h-10" />
    </div>
  )
}

const resultStyles = tv({
  slots: {
    sectionHeader: "type-label-caps text-fg-secondary h-[1.6em] md:h-[2.5em]",
  },
  variants: {
    isSmall: {
      true: {
        sectionHeader: "!h-[1.6em]",
      },
    },
  },
})
